import React, { useState, useEffect } from 'react';
import api from '../../api/axios';
import { jsPDF } from 'jspdf';
import '../../styles/pages.css';

export default function Reports() {
  const [activeTab, setActiveTab] = useState('sent');
  const [sentMessages, setSentMessages] = useState([]);
  const [failedMessages, setFailedMessages] = useState([]);
  const [loading, setLoading] = useState(false);

  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [filteredSentMessages, setFilteredSentMessages] = useState([]);
  const [filteredFailedMessages, setFilteredFailedMessages] = useState([]);
  const [exporting, setExporting] = useState(false);

  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const entityId = user.entity;

  // Fetch messages on mount
  useEffect(() => {
    fetchMessages();
  }, []);

  // Filter messages when dates change
  useEffect(() => {
    filterMessages();
  }, [startDate, endDate, sentMessages, failedMessages]);

  async function fetchMessages() {
    try {
      setLoading(true);
      const [sentRes, failedRes] = await Promise.all([
        api.get(`/api/reports/sent/${entityId}`),
        api.get(`/api/reports/failed/${entityId}`),
      ]);

      setSentMessages(sentRes.data);
      setFailedMessages(failedRes.data);

      console.log('✓ Sent messages:', sentRes.data.length);
      console.log('✓ Failed messages:', failedRes.data.length);
    } catch (error) {
      console.error('Error fetching reports:', error);
    } finally {
      setLoading(false);
    }
  }

  function filterMessages() {
    let filteredSent = [...sentMessages];
    let filteredFailed = [...failedMessages];

    if (startDate) {
      const start = new Date(startDate).getTime();
      filteredSent = filteredSent.filter(m => new Date(m.timePosted).getTime() >= start);
      filteredFailed = filteredFailed.filter(m => new Date(m.timePosted).getTime() >= start);
    }

    if (endDate) {
      const end = new Date(endDate);
      end.setHours(23, 59, 59, 999);
      filteredSent = filteredSent.filter(m => new Date(m.timePosted).getTime() <= end.getTime());
      filteredFailed = filteredFailed.filter(m => new Date(m.timePosted).getTime() <= end.getTime());
    }

    setFilteredSentMessages(filteredSent);
    setFilteredFailedMessages(filteredFailed);
  }

  function clearFilters() {
    setStartDate('');
    setEndDate('');
  }

  function formatDate(dateString) {
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return dateString;
    }
  }

  function exportSentPDF() {
    try {
      setExporting(true);
      const doc = new jsPDF();
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const margin = 15;
      let yPosition = 20;

      // Title
      doc.setFontSize(18);
      doc.setFont(undefined, 'bold');
      doc.text('Sent SMS Report', pageWidth / 2, yPosition, { align: 'center' });
      yPosition += 12;

      // Date range info
      doc.setFontSize(10);
      doc.setFont(undefined, 'normal');
      const dateRange = startDate || endDate
        ? `${startDate || 'Start'} to ${endDate || 'End'}`
        : 'All dates';
      doc.text(`Date Range: ${dateRange}`, margin, yPosition);
      yPosition += 6;
      doc.text(`Generated: ${new Date().toLocaleString()}`, margin, yPosition);
      yPosition += 6;
      doc.text(`Total Records: ${filteredSentMessages.length}`, margin, yPosition);
      yPosition += 10;

      // Table headers
      const colWidths = [35, 80, 55];
      const headers = ['Phone Number', 'Message', 'Date & Time'];
      const col1 = margin;
      const col2 = col1 + colWidths[0];
      const col3 = col2 + colWidths[1];

      // Draw header
      doc.setFont(undefined, 'bold');
      doc.setFontSize(10);
      doc.setFillColor(99, 102, 241);
      doc.setTextColor(255, 255, 255);
      doc.rect(col1, yPosition - 6, pageWidth - 2 * margin, 8, 'F');
      doc.text(headers[0], col1 + 2, yPosition);
      doc.text(headers[1], col2 + 2, yPosition);
      doc.text(headers[2], col3 + 2, yPosition);

      yPosition += 8;
      doc.setTextColor(0, 0, 0);
      doc.setFont(undefined, 'normal');
      doc.setFontSize(9);

      // Draw rows
      filteredSentMessages.forEach((msg, idx) => {
        if (yPosition > pageHeight - 20) {
          // New page
          doc.addPage();
          yPosition = 20;
        }

        // Alternate row color
        if (idx % 2 === 0) {
          doc.setFillColor(245, 245, 245);
          doc.rect(col1, yPosition - 5, pageWidth - 2 * margin, 6, 'F');
        }

        // Text
        doc.text(msg.mobile || '', col1 + 2, yPosition);
        doc.text((msg.message || '').substring(0, 50), col2 + 2, yPosition);
        doc.text(formatDate(msg.timePosted), col3 + 2, yPosition);

        yPosition += 6;
      });

      // Add page numbers
      const pageCount = doc.internal.pages.length - 1;
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setFontSize(8);
        doc.setTextColor(128, 128, 128);
        doc.text(
          `Page ${i} of ${pageCount}`,
          pageWidth / 2,
          pageHeight - 8,
          { align: 'center' }
        );
      }

      doc.save(`sent-sms-${new Date().getTime()}.pdf`);
      console.log('✓ Sent SMS PDF exported successfully');
    } catch (error) {
      console.error('Error exporting PDF:', error);
      alert('Error exporting PDF: ' + error.message);
    } finally {
      setExporting(false);
    }
  }

  function exportFailedPDF() {
    try {
      setExporting(true);
      const doc = new jsPDF();
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const margin = 15;
      let yPosition = 20;

      // Title
      doc.setFontSize(18);
      doc.setFont(undefined, 'bold');
      doc.text('Failed SMS Report', pageWidth / 2, yPosition, { align: 'center' });
      yPosition += 12;

      // Date range info
      doc.setFontSize(10);
      doc.setFont(undefined, 'normal');
      const dateRange = startDate || endDate
        ? `${startDate || 'Start'} to ${endDate || 'End'}`
        : 'All dates';
      doc.text(`Date Range: ${dateRange}`, margin, yPosition);
      yPosition += 6;
      doc.text(`Generated: ${new Date().toLocaleString()}`, margin, yPosition);
      yPosition += 6;
      doc.text(`Total Records: ${filteredFailedMessages.length}`, margin, yPosition);
      yPosition += 10;

      // Table headers
      const colWidths = [35, 80, 55];
      const headers = ['Phone Number', 'Message', 'Date & Time'];
      const col1 = margin;
      const col2 = col1 + colWidths[0];
      const col3 = col2 + colWidths[1];

      // Draw header
      doc.setFont(undefined, 'bold');
      doc.setFontSize(10);
      doc.setFillColor(220, 38, 38);
      doc.setTextColor(255, 255, 255);
      doc.rect(col1, yPosition - 6, pageWidth - 2 * margin, 8, 'F');
      doc.text(headers[0], col1 + 2, yPosition);
      doc.text(headers[1], col2 + 2, yPosition);
      doc.text(headers[2], col3 + 2, yPosition);

      yPosition += 8;
      doc.setTextColor(0, 0, 0);
      doc.setFont(undefined, 'normal');
      doc.setFontSize(9);

      // Draw rows
      filteredFailedMessages.forEach((msg, idx) => {
        if (yPosition > pageHeight - 20) {
          // New page
          doc.addPage();
          yPosition = 20;
        }

        // Alternate row color
        if (idx % 2 === 0) {
          doc.setFillColor(254, 242, 242);
          doc.rect(col1, yPosition - 5, pageWidth - 2 * margin, 6, 'F');
        }

        // Text
        doc.text(msg.mobile || '', col1 + 2, yPosition);
        doc.text((msg.message || '').substring(0, 50), col2 + 2, yPosition);
        doc.text(formatDate(msg.timePosted), col3 + 2, yPosition);

        yPosition += 6;
      });

      // Add page numbers
      const pageCount = doc.internal.pages.length - 1;
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setFontSize(8);
        doc.setTextColor(128, 128, 128);
        doc.text(
          `Page ${i} of ${pageCount}`,
          pageWidth / 2,
          pageHeight - 8,
          { align: 'center' }
        );
      }

      doc.save(`failed-sms-${new Date().getTime()}.pdf`);
      console.log('✓ Failed SMS PDF exported successfully');
    } catch (error) {
      console.error('Error exporting PDF:', error);
      alert('Error exporting PDF: ' + error.message);
    } finally {
      setExporting(false);
    }
  }

  function exportCombinedPDF() {
    try {
      setExporting(true);
      const doc = new jsPDF();
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const margin = 15;
      let yPosition = 20;

      // Title
      doc.setFontSize(18);
      doc.setFont(undefined, 'bold');
      doc.text('SMS Report - Sent & Failed', pageWidth / 2, yPosition, { align: 'center' });
      yPosition += 12;

      // Date range info
      doc.setFontSize(10);
      doc.setFont(undefined, 'normal');
      const dateRange = startDate || endDate
        ? `${startDate || 'Start'} to ${endDate || 'End'}`
        : 'All dates';
      doc.text(`Date Range: ${dateRange}`, margin, yPosition);
      yPosition += 6;
      doc.text(`Generated: ${new Date().toLocaleString()}`, margin, yPosition);
      yPosition += 6;
      doc.text(`Total Sent: ${filteredSentMessages.length} | Total Failed: ${filteredFailedMessages.length}`, margin, yPosition);
      yPosition += 12;

      const colWidths = [35, 80, 55];
      const headers = ['Phone Number', 'Message', 'Date & Time'];
      const col1 = margin;
      const col2 = col1 + colWidths[0];
      const col3 = col2 + colWidths[1];

      // Sent Messages Section
      if (filteredSentMessages.length > 0) {
        doc.setFontSize(12);
        doc.setFont(undefined, 'bold');
        doc.text(`✓ Sent Messages (${filteredSentMessages.length})`, margin, yPosition);
        yPosition += 8;

        // Draw header
        doc.setFont(undefined, 'bold');
        doc.setFontSize(9);
        doc.setFillColor(99, 102, 241);
        doc.setTextColor(255, 255, 255);
        doc.rect(col1, yPosition - 5, pageWidth - 2 * margin, 7, 'F');
        doc.text(headers[0], col1 + 2, yPosition);
        doc.text(headers[1], col2 + 2, yPosition);
        doc.text(headers[2], col3 + 2, yPosition);

        yPosition += 7;
        doc.setTextColor(0, 0, 0);
        doc.setFont(undefined, 'normal');
        doc.setFontSize(8);

        // Draw rows
        filteredSentMessages.forEach((msg, idx) => {
          if (yPosition > pageHeight - 25) {
            doc.addPage();
            yPosition = 20;
          }

          if (idx % 2 === 0) {
            doc.setFillColor(245, 245, 245);
            doc.rect(col1, yPosition - 5, pageWidth - 2 * margin, 5, 'F');
          }

          doc.text(msg.mobile || '', col1 + 2, yPosition);
          doc.text((msg.message || '').substring(0, 50), col2 + 2, yPosition);
          doc.text(formatDate(msg.timePosted), col3 + 2, yPosition);

          yPosition += 5;
        });

        yPosition += 8;
      }

      // Failed Messages Section
      if (filteredFailedMessages.length > 0) {
        doc.setFontSize(12);
        doc.setFont(undefined, 'bold');
        doc.text(`✕ Failed Messages (${filteredFailedMessages.length})`, margin, yPosition);
        yPosition += 8;

        // Draw header
        doc.setFont(undefined, 'bold');
        doc.setFontSize(9);
        doc.setFillColor(220, 38, 38);
        doc.setTextColor(255, 255, 255);
        doc.rect(col1, yPosition - 5, pageWidth - 2 * margin, 7, 'F');
        doc.text(headers[0], col1 + 2, yPosition);
        doc.text(headers[1], col2 + 2, yPosition);
        doc.text(headers[2], col3 + 2, yPosition);

        yPosition += 7;
        doc.setTextColor(0, 0, 0);
        doc.setFont(undefined, 'normal');
        doc.setFontSize(8);

        // Draw rows
        filteredFailedMessages.forEach((msg, idx) => {
          if (yPosition > pageHeight - 25) {
            doc.addPage();
            yPosition = 20;
          }

          if (idx % 2 === 0) {
            doc.setFillColor(254, 242, 242);
            doc.rect(col1, yPosition - 5, pageWidth - 2 * margin, 5, 'F');
          }

          doc.text(msg.mobile || '', col1 + 2, yPosition);
          doc.text((msg.message || '').substring(0, 50), col2 + 2, yPosition);
          doc.text(formatDate(msg.timePosted), col3 + 2, yPosition);

          yPosition += 5;
        });
      }

      // Add page numbers
      const pageCount = doc.internal.pages.length - 1;
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setFontSize(8);
        doc.setTextColor(128, 128, 128);
        doc.text(
          `Page ${i} of ${pageCount}`,
          pageWidth / 2,
          pageHeight - 8,
          { align: 'center' }
        );
      }

      doc.save(`sms-report-${new Date().getTime()}.pdf`);
      console.log('✓ Combined SMS PDF exported successfully');
    } catch (error) {
      console.error('Error exporting PDF:', error);
      alert('Error exporting PDF: ' + error.message);
    } finally {
      setExporting(false);
    }
  }

  const stats = {
    sent: {
      total: sentMessages.length,
      filtered: filteredSentMessages.length,
    },
    failed: {
      total: failedMessages.length,
      filtered: filteredFailedMessages.length,
    },
  };

  return (
    <div className="page-container">
      <div className="page-header">
        <h1>Reports</h1>
        <p>View your sent and failed SMS messages</p>
      </div>

      {/* Filter & Export Section */}
      <div className="reports-filter">
        <div className="filter-inputs">
          <div className="filter-group">
            <label htmlFor="startDate">From Date</label>
            <input
              id="startDate"
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
          </div>
          <div className="filter-group">
            <label htmlFor="endDate">To Date</label>
            <input
              id="endDate"
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
            />
          </div>
          <button className="btn-secondary" onClick={clearFilters} disabled={exporting}>
            Clear Filters
          </button>
        </div>

        <div className="export-buttons">
          <button
            className="btn-export-sent"
            onClick={exportSentPDF}
            disabled={exporting || filteredSentMessages.length === 0}
            title="Export sent messages as PDF"
          >
            {exporting ? '⏳ Exporting...' : '📄 Sent PDF'}
          </button>
          <button
            className="btn-export-failed"
            onClick={exportFailedPDF}
            disabled={exporting || filteredFailedMessages.length === 0}
            title="Export failed messages as PDF"
          >
            {exporting ? '⏳ Exporting...' : '📄 Failed PDF'}
          </button>
          <button
            className="btn-export-combined"
            onClick={exportCombinedPDF}
            disabled={exporting || (filteredSentMessages.length === 0 && filteredFailedMessages.length === 0)}
            title="Export both sent and failed messages as PDF"
          >
            {exporting ? '⏳ Exporting...' : '📄 Combined PDF'}
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="tabs-wrapper">
        <div className="tabs-header">
          <button
            className={`tab-button ${activeTab === 'sent' ? 'active' : ''}`}
            onClick={() => setActiveTab('sent')}
          >
            <span>✓ Sent Messages</span>
            <span className="tab-badge">{stats.sent.total}</span>
          </button>
          <button
            className={`tab-button ${activeTab === 'failed' ? 'active' : ''}`}
            onClick={() => setActiveTab('failed')}
          >
            <span>✕ Failed Messages</span>
            <span className="tab-badge">{stats.failed.total}</span>
          </button>
        </div>

        {/* Sent Messages Tab */}
        {activeTab === 'sent' && (
          <div className="tab-content">
            <div className="stats-row">
              <div className="stat-box">
                <div className="stat-label">Total Sent</div>
                <div className="stat-number">{stats.sent.total}</div>
              </div>
              {startDate || endDate ? (
                <div className="stat-box">
                  <div className="stat-label">Filtered Results</div>
                  <div className="stat-number">{stats.sent.filtered}</div>
                </div>
              ) : null}
            </div>

            {loading ? (
              <div className="loading-message">Loading sent messages...</div>
            ) : filteredSentMessages.length === 0 ? (
              <div className="empty-state">
                <p>📨 No sent messages</p>
                {startDate || endDate ? (
                  <p className="empty-hint">No messages found for the selected date range</p>
                ) : (
                  <p className="empty-hint">Your sent SMS messages will appear here</p>
                )}
              </div>
            ) : (
              <div className="messages-table">
                <table>
                  <thead>
                    <tr>
                      <th>Phone Number</th>
                      <th>Message</th>
                      <th>Date & Time</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredSentMessages.map((msg, idx) => (
                      <tr key={idx}>
                        <td className="mobile-cell">{msg.mobile}</td>
                        <td className="message-cell">{msg.message}</td>
                        <td className="date-cell">{formatDate(msg.timePosted)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Failed Messages Tab */}
        {activeTab === 'failed' && (
          <div className="tab-content">
            <div className="stats-row">
              <div className="stat-box">
                <div className="stat-label">Total Failed</div>
                <div className="stat-number error">{stats.failed.total}</div>
              </div>
              {startDate || endDate ? (
                <div className="stat-box">
                  <div className="stat-label">Filtered Results</div>
                  <div className="stat-number error">{stats.failed.filtered}</div>
                </div>
              ) : null}
            </div>

            {loading ? (
              <div className="loading-message">Loading failed messages...</div>
            ) : filteredFailedMessages.length === 0 ? (
              <div className="empty-state">
                <p>✓ No failed messages</p>
                {startDate || endDate ? (
                  <p className="empty-hint">No failed messages found for the selected date range</p>
                ) : (
                  <p className="empty-hint">Your failed SMS messages will appear here</p>
                )}
              </div>
            ) : (
              <div className="messages-table">
                <table>
                  <thead>
                    <tr>
                      <th>Phone Number</th>
                      <th>Message</th>
                      <th>Date & Time</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredFailedMessages.map((msg, idx) => (
                      <tr key={idx} className="failed-row">
                        <td className="mobile-cell">{msg.mobile}</td>
                        <td className="message-cell">{msg.message}</td>
                        <td className="date-cell">{formatDate(msg.timePosted)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
